/**
 * This file serves as the main provider composition for the application.
 * The 'use client' directive indicates this is a client-side component in Next.js.
 */
'use client';

// External Dependencies
import { setupQueryPersistence } from '@/lib/http/tanstack/persistor';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, type ReactNode } from 'react';

// Internal Providers
import { FEATURE_FLAG_PROVIDER_KEYS } from '@/@feature-flags/config';
import { FeatureFlagsProvider } from '@/@feature-flags/context';
import type { FeatureFlagMap } from '@/@feature-flags/types/primitives';
import StripeProvider from '@/providers/stripe';
import { FontProvider } from './font';
import { UIProvider } from './heroui';
import { LogRocketInitializer } from './logrocket';
import { QueryProvider } from './tanstack';

/**
 * Providers Component
 * Composes all necessary providers for the application
 *
 * `initialFlags`: resolved server-side in the root layout (see
 * src/app/layout.tsx) and threaded down here so `FeatureFlagsProvider` can
 * seed its query with a value already known to be correct, instead of
 * starting from an empty/false default and waiting on a client-only round
 * trip — that gap is what let the pre-migration nav and home page flash on
 * sign-in before the real `home-v2` value arrived.
 */
export function Providers({
    children,
    initialFlags,
}: {
    children: ReactNode;
    initialFlags?: FeatureFlagMap;
}) {
    const router = useRouter();

    const compatibleRouter = useMemo(
        () => ({
            back: router.back,
            forward: router.forward,
            prefetch: () => Promise.resolve(),
            push: router.push,
            refresh: router.refresh,
            replace: router.replace,
        }),
        [router.back, router.forward, router.push, router.refresh, router.replace],
    );

    const queryClient = useMemo(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
                        refetchOnWindowFocus: true,
                        refetchOnMount: true,
                        retry: 1,
                    },
                },
            }),
        [],
    );

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setupQueryPersistence(queryClient);
        }
    }, [queryClient]);

    return (
        <LogRocketInitializer>
            <QueryProvider>
                <QueryClientProvider client={queryClient}>
                    <UIProvider navigate={compatibleRouter}>
                        <FontProvider>
                            <FeatureFlagsProvider
                                keys={FEATURE_FLAG_PROVIDER_KEYS}
                                initialFlags={initialFlags}
                            >
                                <StripeProvider>{children}</StripeProvider>
                            </FeatureFlagsProvider>
                        </FontProvider>
                        <ReactQueryDevtools initialIsOpen={false} />
                    </UIProvider>
                </QueryClientProvider>
            </QueryProvider>
        </LogRocketInitializer>
    );
}

Providers.displayName = 'Providers';
